import Vue from "vue";
import * as Sentry from "@sentry/vue";

const ENABLED = process.env.VUE_APP_SENTRY_ENABLED === "true";

function redactSensitiveFields(value) {
  if (!value || typeof value !== "object") return value;

  if (Array.isArray(value)) {
    value.forEach((item) => redactSensitiveFields(item));
    return value;
  }

  Object.keys(value).forEach((key) => {
    const fieldValue = value[key];
    if (/token|password|secret|api[_-]?key|authorization|cookie/i.test(key)) {
      value[key] = "[REDACTED]";
      return;
    }
    if (fieldValue && typeof fieldValue === "object") {
      redactSensitiveFields(fieldValue);
    }
  });

  return value;
}

export function initSentry(router) {
  if (!ENABLED) return;

  Sentry.init({
    Vue,
    dsn: process.env.VUE_APP_SENTRY_DSN_FRONTEND,
    environment: process.env.VUE_APP_ENV || "development",
    release: process.env.VUE_APP_RELEASE || "unknown",
    attachStacktrace: true,
    integrations: [
      Sentry.browserTracingIntegration({ router }),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],
    // 10% of transactions (how long something took) in production, 100% in staging/dev
    tracesSampleRate: process.env.VUE_APP_ENV === "production" ? 0.1 : 1.0,
    // Allow Sentry trace headers to be propagated to local + cloud function endpoints.
    tracePropagationTargets: [
      "localhost",
      /^https:\/\/.*\.cloudfunctions\.net/,
      /^https:\/\/.*-.*\.a\.run\.app/,
    ],
    // Record 1% of sessions normally; 100% of sessions where an error occurs
    replaysSessionSampleRate: 0.01,
    replaysOnErrorSampleRate: 1.0,
    // Remove or redact sensitive fields before the event is sent to Sentry.
    beforeSend(event) {
      if (event.request?.headers) {
        delete event.request.headers.Authorization;
        delete event.request.headers.Cookie;
        delete event.request.headers["X-API-Key"];
      }

      if (event.user) {
        delete event.user.email;
        delete event.user.ip_address;
      }

      if (event.extra) redactSensitiveFields(event.extra);
      if (event.contexts) redactSensitiveFields(event.contexts);

      return event;
    },
    // Scrub sensitive headers from performance/tracing transactions (beforeSend only covers errors).
    beforeSendTransaction(transaction) {
      if (transaction.request?.headers) {
        delete transaction.request.headers.Authorization;
        delete transaction.request.headers.Cookie;
        delete transaction.request.headers["X-API-Key"];
      }
      return transaction;
    },
    // Suppress known non-actionable Vue Router navigation errors
    ignoreErrors: [
      "NavigationDuplicated",                 // same route navigated twice (Vue Router)
      "Avoided redundant navigation",         // same as above, different message
      "ResizeObserver loop limit exceeded",   // browser resize observer quirk
      "Network Error",                        // fetch failed; usually offline / bad connection
      "ChunkLoadError",                       // stale tab after deploy; user needs hard refresh
    ],
  });
}

// Call after the user authenticates (inside onAuthStateChanged)
export function identifyUser(user, claims) {
  if (!ENABLED) return;
  // email is intentionally omitted — uid is sufficient for deduplication and
  // beforeSend would strip it anyway. Avoids a misleading set-then-delete pattern.
  Sentry.setUser({
    id: user.uid,
  });
  Sentry.setTag("userRole", claims.role);
  Sentry.setTag("accountType", claims.accountType);
  Sentry.setTag(
    "brand",
    Array.isArray(claims.brands) ? claims.brands[0] : null
  );
  Sentry.setTag("state", claims.state);
}

// Call when the user logs out
export function clearUser() {
  if (!ENABLED) return;
  Sentry.setUser(null);
}

// ─── Workflow Error Logging ────────────────────────────────────────────────
// "workflow" → one of: sales-order, work-order, sourcing, manufacturing, etc
// "action"   → what was happening: "submit", "approve", "create-transfer"
// "context"  → any extra data useful for debugging (order ID, etc.)

export function logWorkflowError(error, { workflow, action, context = {} }) {
  if (!ENABLED) {
    console.error(`[${workflow}/${action}]`, error, context);
    return;
  }
  Sentry.withScope((scope) => {
    scope.setTag("workflow", workflow);
    scope.setTag("action", action);
    scope.setContext("workflow_context", context);
    Sentry.captureException(error);
  });
}

// For non-fatal issues you want to track (e.g. a value came back null but you recovered)
export function logWorkflowWarning(
  message,
  { workflow, action, context = {} }
) {
  if (!ENABLED) {
    console.warn(`[${workflow}/${action}] ${message}`, context);
    return;
  }
  Sentry.withScope((scope) => {
    scope.setTag("workflow", workflow);
    scope.setTag("action", action);
    scope.setLevel("warning");
    scope.setContext("workflow_context", context);
    Sentry.captureMessage(message);
  });
}

// Step-by-step breadcrumb trail — Sentry keeps the last 100 and shows them
// alongside any error that fires after them, so you can see what led up to the crash.
export function addBreadcrumb(message, { workflow, data = {} }) {
  if (!ENABLED) return;
  Sentry.addBreadcrumb({
    category: workflow,
    message,
    data,
    level: "info",
  });
}

// Call from any component to verify Sentry is receiving frontend events.
export function smokeTest() {
  if (!ENABLED) {
    console.warn("[Sentry] Disabled — set VUE_APP_SENTRY_ENABLED=true to test");
    return;
  }
  Sentry.captureException(new Error("[Sentry smoke test] intentional — safe to resolve"));
  console.info("[Sentry] Smoke test sent. Check mytrace-frontend in Sentry.");
}
